import React from 'react';
import { MapPin, Mail, Users, Target, Heart, Trophy } from 'lucide-react';

const About = () => {
  const clubLogo = "https://customer-assets.emergentagent.com/job_cafc-platform/artifacts/d9rcitex_OurLogo.png";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img 
            src={clubLogo} 
            alt="Crawley Athletic FC" 
            className="h-20 w-20 mx-auto mb-6 filter invert"
          />
          <h1 className="text-4xl font-bold mb-4">About Crawley Athletic</h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            Crawley Athletic FC offers open-access football for players of all abilities and builds a welcoming, inclusive community for them and their families.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Our Story */}
        <section className="mb-16">
          <div className="card">
            <h2 className="text-3xl font-bold mb-6">Our Story</h2>
            <div className="prose prose-lg text-gray-600">
              <p className="mb-4">
                Crawley Athletic Football Club was established in 2025 with a simple yet powerful vision: 
                to create a community-centered football club that embodies the spirit and pride of Crawley. 
                Born from the passion of local football enthusiasts, our club represents more than just a team – 
                we are a symbol of unity, determination, and sporting excellence.
              </p>
              <p className="mb-4">
                From our humble beginnings, we have been committed to developing not only skilled footballers 
                but also outstanding individuals who carry the values of teamwork, respect, and community service 
                both on and off the pitch. Our distinctive black and white colours reflect our unwavering commitment 
                to excellence and the proud heritage of our town.
              </p>
              <p>
                As we continue to grow, Crawley Athletic remains dedicated to providing opportunities for players 
                of all ages and abilities, fostering local talent, and bringing our community together through 
                the beautiful game of football. We are more than a club – we are a family.
              </p>
            </div>
          </div>
        </section>

        {/* Mission & Values */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Our Mission & Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card text-center">
              <Target className="h-12 w-12 mx-auto mb-4 text-black" />
              <h3 className="text-xl font-semibold mb-3">Excellence</h3>
              <p className="text-gray-600">
                We strive for excellence in everything we do, from player development 
                to community engagement, always aiming to be the best version of ourselves.
              </p>
            </div>
            
            <div className="card text-center">
              <Heart className="h-12 w-12 mx-auto mb-4 text-black" />
              <h3 className="text-xl font-semibold mb-3">Community</h3>
              <p className="text-gray-600">
                At the heart of everything we do is our commitment to the Crawley community, 
                bringing people together through sport and shared passion.
              </p>
            </div>
            
            <div className="card text-center">
              <Trophy className="h-12 w-12 mx-auto mb-4 text-black" />
              <h3 className="text-xl font-semibold mb-3">Achievement</h3>
              <p className="text-gray-600">
                We celebrate success in all its forms, recognizing that true achievement 
                comes from dedication, teamwork, and never giving up.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="mb-16">
          <div className="card">
            <h2 className="text-3xl font-bold mb-8 text-center">Get In Touch</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Contact Details */}
              <div>
                <h3 className="text-xl font-semibold mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <Mail className="h-5 w-5 text-gray-400 mt-1 mr-3" />
                    <div>
                      <p className="font-medium">General Enquiries</p>
                      <a 
                        href="mailto:info@crawleyafc.uk" 
                        className="text-black hover:underline"
                      >
                        info@crawleyafc.uk
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Mail className="h-5 w-5 text-gray-400 mt-1 mr-3" />
                    <div>
                      <p className="font-medium">Shop & Sales</p>
                      <a 
                        href="mailto:sales@crawleyafc.uk" 
                        className="text-black hover:underline"
                      >
                        sales@crawleyafc.uk
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <MapPin className="h-5 w-5 text-gray-400 mt-1 mr-3" />
                    <div>
                      <p className="font-medium">Stadium Address</p>
                      <p className="text-gray-600">
                        Crawley Athletic FC Stadium<br />
                        Crawley, West Sussex<br />
                        United Kingdom
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Training & Stadium Info */}
              <div>
                <h3 className="text-xl font-semibold mb-4">Stadium & Training</h3>
                <div className="space-y-4">
                  <div>
                    <p className="font-medium mb-2">Training Schedule</p>
                    <div className="text-gray-600 space-y-1">
                      <p>Vets Team: Thursday 8:00 PM</p>
                      <p>Youth Teams: TBC</p>
                      <p>Games (Vets): Sunday</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="font-medium mb-2">Facilities</p>
                    <div className="text-gray-600 space-y-1">
                      <p>Full-size grass/3G pitch</p>
                      <p>Changing rooms</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Join Us CTA */}
        <section className="text-center">
          <div className="bg-black text-white rounded-lg p-8">
            <Users className="h-16 w-16 mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-4">Join Our Family</h2>
            <p className="text-xl text-gray-200 mb-6 max-w-2xl mx-auto">
              Whether you want to play, volunteer, or support the club, 
              there's always room for more passionate people in the Crawley Athletic family.
            </p>
            <a href="/join-us" className="btn btn-white">
              Get Involved
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;